// frontend/src/components/Header/Header.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAuth, signOut } from 'firebase/auth';
import { useUserAuth } from "../../context/UserAuthContext";
import { FaUser, FaSignOutAlt, FaUserEdit, FaTrashAlt } from "react-icons/fa";
import "./Header.css";
import menuicon from "../../images/icons/menuicon.jpg";
import cross from "../../images/icons/cross.jpg";

const Header = () => {
  const { t, i18n } = useTranslation();
  const { user, userProfile } = useUserAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const lastScrollY = useRef(0);
  const headerRef = useRef(null);
  const navLinksRef = useRef(null);
  const menuToggleRef = useRef(null);
  const userMenuRef = useRef(null);

  // Check if user is admin
  useEffect(() => {
    if (userProfile && userProfile.role === 'admin') {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
  }, [userProfile]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 50);

      if (currentScrollY < 50) {
        setVisible(true);
        return;
      }

      if (currentScrollY > lastScrollY.current) {
        setVisible(false);
      } else {
        setVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close main menu if clicked outside
      if (
          menuOpen &&
          navLinksRef.current &&
          !navLinksRef.current.contains(event.target) &&
          !menuToggleRef.current.contains(event.target)
      ) {
        closeMenu();
      }
      
      // Close user dropdown if clicked outside (desktop)
      if (
        showUserMenu &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target) &&
        window.innerWidth > 768 // Only for desktop
      ) {
        setShowUserMenu(false);
      }
      
      // On mobile, we manage the dropdown click in the onClick handler
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen, showUserMenu]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        if (menuOpen) closeMenu();
        if (showUserMenu) setShowUserMenu(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [menuOpen, showUserMenu]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && menuOpen) {
        closeMenu();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [menuOpen]);

  const toggleMenu = (e) => {
    e.preventDefault();
    const newMenuState = !menuOpen;
    setMenuOpen(newMenuState);
    document.body.style.overflow = newMenuState ? "hidden" : "";

    if (menuToggleRef.current) {
      menuToggleRef.current.setAttribute("aria-expanded", String(newMenuState));
    }

    if (newMenuState && navLinksRef.current) {
      const navItems = navLinksRef.current.querySelectorAll("li");
      navItems.forEach((item, index) => {
        item.style.transitionDelay = `${0.1 * index}s`;
        item.classList.add("fade-in");
      });
    }
  };

  const closeMenu = () => {
    if (menuOpen) {
      setMenuOpen(false);
      document.body.style.overflow = "";

      if (menuToggleRef.current) {
        menuToggleRef.current.setAttribute("aria-expanded", "false");
      }

      if (navLinksRef.current) {
        const navItems = navLinksRef.current.querySelectorAll("li");
        navItems.forEach(item => {
          item.style.transitionDelay = "0s";
          item.classList.remove("fade-in");
        });
      }
      
      // Also close the user dropdown when closing the menu
      setShowUserMenu(false);
    }
  };
  
  // Language change function removed as it's handled in footer

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setShowUserMenu(false);
      if (menuOpen) closeMenu();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Helper function to check if a link is active
  const isActiveLink = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
      <header
          className={`main-header ${!visible ? "header-hidden" : ""} ${scrolled ? "scrolled" : ""}`}
          ref={headerRef}
      >
        <nav className="nav-menu">
          <div className="logo">
            <Link to="/" className={isActiveLink('/') ? 'active' : ''}>{t('header.brand')}</Link>
          </div>

          <button
              ref={menuToggleRef}
              className={`mobile-menu-toggle ${menuOpen ? "active" : ""}`}
              onClick={toggleMenu}
              aria-label={t('header.toggleMenu')}
              aria-expanded="false"
          >
            <img
                src={menuicon}
                alt={t('header.menuIconAlt')}
                className="menu-icon"
            />
            <img
                src={cross}
                alt={t('header.closeIconAlt')}
                className="close-icon"
            />
          </button>

          <ul className={`nav-links ${menuOpen ? "active" : ""}`} ref={navLinksRef}>
            <li>
              <Link 
                to="/travel" 
                onClick={closeMenu}
                className={isActiveLink('/travel') ? 'active' : ''}
              >
                {t('header.travelStay')}
              </Link>
            </li>
            <li>
              <Link 
                to="/registry" 
                onClick={closeMenu}
                className={isActiveLink('/registry') ? 'active' : ''}
              >
                {t('header.registry')}
              </Link>
            </li>
            <li>
              <Link 
                to="/photos" 
                onClick={closeMenu}
                className={isActiveLink('/photos') ? 'active' : ''}
              >
                {t('photoGallery.title')}
              </Link>
            </li>
            <li>
              <Link 
                to="/wishes" 
                onClick={closeMenu}
                className={isActiveLink('/wishes') ? 'active' : ''}
              >
                {t('wishes.title')}
              </Link>
            </li>
            
            {/* User Menu for Mobile */}
            <li className="mobile-only">
              {user ? (
                <div className="mobile-user-dropdown">
                  <button 
                    className="mobile-dropdown-toggle"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowUserMenu(!showUserMenu);
                    }}
                  >
                    <FaUser className="mobile-icon" />
                    <span>{user.displayName || user.email || user.phoneNumber || t('guest')}</span>
                    <span className="dropdown-arrow">{showUserMenu ? '▲' : '▼'}</span>
                  </button>
                  
                  {showUserMenu && (
                    <div className="mobile-dropdown-menu">
                      <Link to="/profile" onClick={closeMenu} className="mobile-dropdown-item">
                        <FaUserEdit className="mobile-icon" />
                        <span>{t('footer.editProfile')}</span>
                      </Link>
                      
                      {isAdmin && (
                        <Link to="/admin" onClick={closeMenu} className="mobile-dropdown-item">
                          <FaUser className="mobile-icon" />
                          <span>{t('adminDashboard.title')}</span>
                        </Link>
                      )}
                      
                      <Link to="/profile/delete-account" onClick={closeMenu} className="mobile-dropdown-item delete-account-item">
                        <FaTrashAlt className="mobile-icon" />
                        <span>{t('accountDeletion.deleteAccount')}</span>
                      </Link>
                      
                      <button onClick={handleLogout} className="mobile-dropdown-item logout-btn">
                        <FaSignOutAlt className="mobile-icon" />
                        <span>{t('footer.logout')}</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" onClick={closeMenu} className="mobile-login-btn">
                  <FaSignOutAlt className="mobile-icon" />
                  {t('login')}
                </Link>
              )}
            </li>
            
            {/* Language menu removed - now handled in footer */}
          </ul>

          <div className="header-actions">
            {/* User Dropdown */}
            <div className="dropdown user-dropdown" ref={userMenuRef}>
              <button 
                className="dropdown-toggle" 
                onClick={() => setShowUserMenu(!showUserMenu)}
                aria-expanded={showUserMenu}
              >
                <FaUser className="user-icon" />
              </button>
              {showUserMenu && (
                <div className="dropdown-menu show">
                  {user ? (
                    <>
                      <div className="dropdown-item user-info">
                        <FaUser className="dropdown-icon" />
                        <div>
                          <span className="user-name">{user.displayName || user.email || user.phoneNumber || t('guest')}</span>
                          {isAdmin && <span className="user-role">{t('admin')}</span>}
                        </div>
                      </div>
                      <div className="dropdown-divider"></div>
                      
                      <Link to="/profile" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                        <FaUserEdit className="dropdown-icon" />
                        <span>{t('footer.editProfile')}</span>
                      </Link>
                      
                      {isAdmin && (
                        <Link to="/admin" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                          <FaUser className="dropdown-icon" />
                          <span>{t('adminDashboard.title')}</span>
                        </Link>
                      )}
                      
                      <Link to="/profile/delete-account" className="dropdown-item delete-account-item" onClick={() => setShowUserMenu(false)}>
                        <FaTrashAlt className="dropdown-icon" />
                        <span>{t('accountDeletion.deleteAccount')}</span>
                      </Link>
                      
                      <button 
                        className="dropdown-item logout-btn" 
                        onClick={handleLogout}
                      >
                        <FaSignOutAlt className="dropdown-icon" />
                        <span>{t('footer.logout')}</span>
                      </button>
                    </>
                  ) : (
                    <Link to="/login" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                      <FaSignOutAlt className="dropdown-icon" />
                      <span>{t('login')}</span>
                    </Link>
                  )}
                </div>
              )}
            </div>
            
            {/* Language Dropdown removed - now handled in footer */}
            
            {/* Primary CTA. Was RSVP until the wedding passed; the most
                prominent button on the site now points at the newest news
                rather than a closed form. */}
            <div className={`cta-button ${menuOpen ? 'menu-open' : ''}`}>
              <Link
                to="/baby-reveal"
                className={`rsvp-btn ${isActiveLink('/baby-reveal') ? 'active' : ''}`}
                onClick={closeMenu}
              >
                {t('header.babyReveal', 'Baby reveal')}
              </Link>
            </div>
          </div>
        </nav>
      </header>
  );
};

export default Header;